import React, { useState, useEffect, useRef } from "react";
import { X, Search } from "lucide-react";
import { Link } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import ResponseMessage from "../common/ResponseMessage";
import type { MedicalRecord, Patient, Doctor } from "../../types";
import type { Medication } from "../../types/medication";
import type { Analyse } from "../../types/analyse";
import { AddedItemsSection } from "../consultation/CurrentConsultationStepComponents/AddedItemsSection";
import { AnalyseModal } from "../consultation/CurrentConsultationStepComponents/AnalyseModal";
import { ActionButtons } from "../consultation/CurrentConsultationStepComponents/ActionButtons";
import MedicalPrescriptionInterface from "../consultation/MedicalPrescriptionInterface";
import { Modal } from "../ui/Modal";
import { useModalsManager } from "../../hooks/useModalsManager";
import { useAnalysesManager } from "../../hooks/useAnalysesManager";
import { useConfirm } from "../../context/ConfirmContext";
import { toast } from "sonner";

type MinimalPatient = Pick<Patient, "id" | "firstName" | "lastName" | "phone">;

interface MedicalRecordFormBaseProps {
  mode: "create" | "update";
  initialData?: MedicalRecord;
  onSubmit: (
    data: MedicalRecord,
    prescriptionFile?: File | null,
    resultFile?: File | null
  ) => Promise<void>;
  isSubmitting: boolean;
}

const MedicalRecordFormBase: React.FC<MedicalRecordFormBaseProps> = ({
  mode,
  initialData,
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<MedicalRecord>({
    id: initialData?.id || 0,
    // Extract motif from symptoms if motif field is empty (migration logic for old records)
    symptoms: (() => {
      let symptoms = initialData?.symptoms || "";
      if (initialData?.motif) {
        return symptoms;
      }
      // If motif is empty, try to extract it from symptoms
      const motifPatterns = [
        'GENERAL_CONSULTATION',
        'PEDIATRIC_CONSULTATION', 
        'SPECIALIST_CONSULTATION',
        'RESPIRATORY_PHYSIOTHERAPY',
        'PRE_EMPLOYMENT_VISIT',
        'GYNECOLOGY_CONSULTATION'
      ];
      
      for (const pattern of motifPatterns) {
        if (symptoms.includes(pattern)) {
          symptoms = symptoms
            .replace(new RegExp(pattern, 'g'), '') // Remove all occurrences
            .replace(/,\s*,\s*/g, ', ') // Fix double commas
            .replace(/,\s*,\s*/g, ', ') // Again for safety
            .replace(/^,\s*/, '') // Remove leading comma
            .replace(/,\s*$/, '') // Remove trailing comma
            .replace(/\s*,\s*/g, ', ') // Normalize spaces around commas
            .replace(/\s{2,}/g, ' ') // Remove multiple spaces
            .trim();
          break;
        }
      }
      return symptoms;
    })(),
    motif: (() => {
      // If motif exists in data, use it
      if (initialData?.motif) {
        return initialData.motif;
      }
      // Otherwise try to extract it from symptoms
      const symptoms = initialData?.symptoms || "";
      const motifPatterns = [
        'GENERAL_CONSULTATION',
        'PEDIATRIC_CONSULTATION', 
        'SPECIALIST_CONSULTATION',
        'RESPIRATORY_PHYSIOTHERAPY',
        'PRE_EMPLOYMENT_VISIT',
        'GYNECOLOGY_CONSULTATION'
      ];
      
      for (const pattern of motifPatterns) {
        if (symptoms.includes(pattern)) {
          return pattern;
        }
      }
      return "";
    })(),
    symptomDuration: initialData?.symptomDuration || "",
    currentMedication: initialData?.currentMedication || "",
    preexistingCondition: initialData?.preexistingCondition || "",
    familyHistory: initialData?.familyHistory || "",
    allergies: initialData?.allergies || "",
    assessment: initialData?.assessment || "",
    observations: initialData?.observations || "",
    recommendation: initialData?.recommendation || "",
    prescriptionUrl: initialData?.prescriptionUrl ?? undefined,
    resultUrl: initialData?.resultUrl ?? undefined,
    patientId: initialData?.patientId || 0,
    patientFullName: initialData?.patientFullName || "",
    phone: initialData?.phone || "",
    dateCreation: initialData?.dateCreation || "",
    doctorId: initialData?.doctorId || 0,
    doctorName: initialData?.doctorName || "",
    consultationFee: initialData?.consultationFee || 0,
    amountPaid: initialData?.amountPaid || 0,
    amountDue: initialData?.amountDue || 0,
  });

  const [prescriptionFile, setPrescriptionFile] = useState<File | null>(null);
  const [resultFile, setResultFile] = useState<File | null>(null);
  const [patients, setPatients] = useState<MinimalPatient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<MinimalPatient[]>([]);
  const [searchPhone, setSearchPhone] = useState(initialData?.phone || "");
  const [selectedPatientName, setSelectedPatientName] = useState(
    initialData?.patientFullName || ""
  );
  const [responseMessage, setResponseMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const doctorsLoaded = useRef(false);
  const patientsLoaded = useRef(false);

  // Advanced managers
  const [medications, setMedications] = useState<Medication[]>(
    initialData?.prescriptionItems?.map((m: any) => ({
      name: m.name,
      dose: m.dose,
      doseUnit: m.doseUnit,
      frequency: m.frequency,
      interval: m.interval,
      duration: m.duration,
      durationUnit: m.durationUnit,
      instructions: m.instructions,
      id: m.id || 0,
      orderNumber: m.orderNumber || "",
      quantity: m.quantity || 1,
      quantityUnit: m.quantityUnit || "Unités",
      startDate: m.startDate || "",
      endDate: m.endDate || "",
      advice: m.advice || "",
      priority: m.priority || "normal"
    })) as Medication[] || []
  );
  const [analyses, setAnalyses] = useState<Analyse[]>(
    initialData?.additionalExaminations?.map((a: any) => ({
      label: a.examName,
      nom: a.examName,
      reason: a.reason,
      diagnostics: a.concernedDiagnostics,
      conseils: a.patientAdvice
    })) as Analyse[] || []
  );

  const modalsManager = useModalsManager();
  const analysesManager = useAnalysesManager(analyses, setAnalyses);
  const { confirm } = useConfirm();

  const handleAddMedication = (med: Medication) => {
    setMedications([...medications, med]);
  };

  const handleDeleteMedication = async (index: number) => {
    const isConfirmed = await confirm("Êtes-vous sûr de vouloir supprimer ce médicament ?", {
      title: "Supprimer",
      isDangerous: true
    });
    if (isConfirmed) {
      setMedications(medications.filter((_, i) => i !== index));
      toast.success("Médicament supprimé");
    }
  };

  const handleAddAnalyse = () => {
    analysesManager.handleAddAnalyse();
    modalsManager.openAnalyseModal();
  };

  const handleEditAnalyse = (index: number) => {
    analysesManager.handleEditAnalyse(index);
    modalsManager.openAnalyseModal();
  };

  const handleAnalyseSubmit = () => {
    const success = analysesManager.handleAnalyseSubmit();
    if (success) modalsManager.closeAnalyseModal();
  };

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");

        const doctorsRes = await axiosInstance.get(
          API_ENDPOINTS.USERS.DOCTORS.LIST
        );
        setDoctors(doctorsRes.data);
      } catch (error) {
        setResponseMessage("Erreur lors du chargement des docteurs.");
        console.error("Error fetching doctors:", error);
      } finally {
        if (!doctorsLoaded.current) {
          doctorsLoaded.current = true;
          if (patientsLoaded.current) {
            setLoading(false);
          }
        }
      }
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    let isActive = true;
    const debounce = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          todayOnly: "false",
          showConsulted: "true",
          page: "1",
          size: "50",
        });
        if (searchPhone.trim()) {
          params.append("search", searchPhone.trim());
        }

        const patientsRes = await axiosInstance.get(
          `${API_ENDPOINTS.CONSTANTES.LIST}?${params.toString()}`
        );

        if (!isActive) return;

        const items = patientsRes.data?.items || patientsRes.data || [];
        const uniquePatients = new Map<number, MinimalPatient>();

        items.forEach((constante: any) => {
          const patientId = constante.patient?.id || constante.patientId;
          if (!patientId || uniquePatients.has(patientId)) {
            return;
          }

          uniquePatients.set(patientId, {
            id: patientId,
            firstName:
              constante.patient?.firstName ||
              constante.firstName ||
              "Patient",
            lastName:
              constante.patient?.lastName || constante.lastName || "Inconnu",
            phone: constante.patient?.phone || constante.patientPhone || "",
          });
        });

        const mappedPatients = Array.from(uniquePatients.values());
        setPatients(mappedPatients);
        setFilteredPatients(mappedPatients);
        if (!patientsLoaded.current) {
          patientsLoaded.current = true;
          if (doctorsLoaded.current) {
            setLoading(false);
          }
        }
      } catch (error) {
        if (isActive) {
          setResponseMessage("Erreur lors du chargement des patients.");
          console.error("Error fetching patients:", error);
          if (!patientsLoaded.current) {
            patientsLoaded.current = true;
            if (doctorsLoaded.current) {
              setLoading(false);
            }
          }
        }
      }
    }, 300);

    return () => {
      isActive = false;
      clearTimeout(debounce);
    };
  }, [searchPhone]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "patientId") {
      const selected = patients.find((p) => p.id.toString() === value);
      if (selected) {
        setSelectedPatientName(`${selected.firstName} ${selected.lastName}`);
        setFormData((prev) => ({
          ...prev,
          patientFullName: `${selected.firstName} ${selected.lastName}`,
          phone: selected.phone,
        }));
      } else {
        setSelectedPatientName("");
        setFormData((prev) => ({
          ...prev,
          patientFullName: "",
          phone: "",
        }));
      }
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "prescription" | "result"
  ) => {
    const file = e.target.files?.[0] || null;
    if (type === "prescription") setPrescriptionFile(file);
    if (type === "result") setResultFile(file);
  };

  const handleSearchPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhone(e.target.value);
  };

  const handleResetFilter = () => {
    setSearchPhone("");
    setFilteredPatients(patients);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare the final data including simplified lists
      const finalData: any = {
        ...formData,
        prescriptionItems: medications.map(m => ({
          name: m.name,
          dose: m.dose,
          doseUnit: m.doseUnit,
          frequency: m.frequency,
          interval: m.interval,
          duration: m.duration,
          durationUnit: m.durationUnit,
          instructions: m.instructions,
          quantity: m.quantity || 1,
          quantityUnit: m.quantityUnit || "Unités"
        })),
        additionalExaminations: analyses.map(a => ({
          examName: a.label || a.nom,
          reason: a.reason,
          concernedDiagnostics: a.diagnostics,
          patientAdvice: a.conseils
        }))
      };
      await onSubmit(finalData, prescriptionFile, resultFile);
    } catch (error) {
      setResponseMessage("Erreur lors de la soumission du formulaire.");
      console.error("Error submitting form:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-gray-600">Chargement des données...</div>
      </div>
    );
  }

  return (
    <section className="bg-white rounded-2xl shadow-lg p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-800">
          {mode === "create"
            ? "Enregistrer un dossier médical"
            : "Mettre à jour un dossier médical"}
        </h3>
        <Link
          to="/doctor/dashboard/record"
          className="text-gray-500 hover:text-gray-700"
          aria-label="Annuler"
        >
          <X size={24} />
        </Link>
      </div>

      <div className="mb-6 flex items-center gap-3">
        <div className="relative flex-grow">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            value={searchPhone}
            onChange={handleSearchPhone}
            placeholder="Rechercher un téléphone..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>
        <button
          type="button"
          onClick={handleResetFilter}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Réinitialiser
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-6"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motif de consultation *
          </label>
          <input
            type="text"
            name="motif"
            value={formData.motif || ""}
            onChange={handleChange}
            placeholder="Motif de la consultation"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
            aria-required="true"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Symptômes *
          </label>
          <input
            type="text"
            name="symptoms"
            value={formData.symptoms}
            onChange={handleChange}
            placeholder="Symptômes actuels (sans les conditions préexistantes)"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
            aria-required="true"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Durée des symptômes *
          </label>
          <input
            type="text"
            name="symptomDuration"
            value={formData.symptomDuration}
            onChange={handleChange}
            placeholder="Durée des symptômes"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
            aria-required="true"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Médicaments actuels
          </label>
          <input
            type="text"
            name="currentMedication"
            value={formData.currentMedication}
            onChange={handleChange}
            placeholder="Médicaments actuels"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Conditions préexistantes
          </label>
          <input
            type="text"
            name="preexistingCondition"
            value={formData.preexistingCondition}
            onChange={handleChange}
            placeholder="Conditions préexistantes (ex: Hypertension, Diabète, AVC)"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Antécédents familiaux
          </label>
          <input
            type="text"
            name="familyHistory"
            value={formData.familyHistory}
            onChange={handleChange}
            placeholder="Antécédents familiaux"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Allergies
          </label>
          <input
            type="text"
            name="allergies"
            value={formData.allergies}
            onChange={handleChange}
            placeholder="Allergies"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="sm:col-span-2 space-y-6 mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
          <h4 className="text-lg font-bold text-slate-800 border-b pb-2">
            Évaluation Médicale
          </h4>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
              1
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observations *
              </label>
              <textarea
                name="observations"
                value={formData.observations || ""}
                onChange={handleChange}
                placeholder="Observations du médecin lors de l'examen (signes vitaux, état général, examen physique...)"
                className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                required
                aria-required="true"
              />
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
              2
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Diagnostic *
              </label>
              <textarea
                name="assessment"
                value={formData.assessment}
                onChange={handleChange}
                placeholder="Diagnostic médical basé sur les symptômes et observations"
                className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                required
                aria-required="true"
              />
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
              3
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recommandations *
              </label>
              <textarea
                name="recommendation"
                value={formData.recommendation}
                onChange={handleChange}
                placeholder="Recommandations et plan de traitement"
                className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                required
                aria-required="true"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Téléphone du patient *
          </label>
          <select
            name="patientId"
            value={formData.patientId}
            onChange={handleChange}
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
            aria-required="true"
          >
            <option value="">
              -- Sélectionner un patient par téléphone --
            </option>
            {filteredPatients.map((patient) => (
              <option key={patient.id} value={patient.id}>
                {patient.phone} - {patient.firstName} {patient.lastName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom complet du patient
          </label>
          <input
            type="text"
            value={selectedPatientName}
            readOnly
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Médecin assigné
          </label>
          <select
            name="doctorId"
            value={formData.doctorId}
            onChange={handleChange}
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
            aria-required="true"
          >
            <option value="">-- Sélectionner un médecin --</option>
            {doctors.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                Dr. {doctor.firstName} {doctor.lastName} -{" "}
                {doctor.specialty}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Frais de consultation
          </label>
          <input
            type="number"
            name="consultationFee"
            value={formData.consultationFee}
            onChange={handleChange}
            placeholder="Frais de consultation"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Montant Payé
          </label>
          <input
            type="number"
            name="amountPaid"
            value={formData.amountPaid}
            onChange={handleChange}
            placeholder="Montant Payé"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reste à payer (Impayé)
          </label>
          <input
            type="number"
            value={(Number(formData.consultationFee || 0) - Number(formData.amountPaid || 0)).toFixed(2)}
            readOnly
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fichier d'ordonnance (Image/PDF)
          </label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, "prescription")}
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fichier de résultats (Image/PDF)
          </label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, "result")}
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50"
          />
        </div>

        {/* New sections for medications and analyses */}
        <div className="sm:col-span-2 space-y-6 mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
          <h4 className="text-lg font-bold text-slate-800 border-b pb-2">
            Gestion des Prescriptions et Analyses
          </h4>

          <AddedItemsSection
            analyses={analyses}
            medications={medications}
            onAddAnalyse={handleAddAnalyse}
            onEditAnalyse={handleEditAnalyse}
            onDeleteAnalyse={analysesManager.handleDeleteAnalyse}
            onAddMedication={modalsManager.openPrescriptionModal}
            onDeleteMedication={handleDeleteMedication}
          />

          <ActionButtons
            onAddAnalyse={handleAddAnalyse}
            onAddPrescription={modalsManager.openPrescriptionModal}
          />
        </div>

        <div className="sm:col-span-2 flex justify-end gap-3 mt-6">
          <Link
            to="/doctor/dashboard/record"
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            aria-label="Annuler"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-colors ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
          >
            {isSubmitting
              ? "Enregistrement..."
              : mode === "create"
                ? "Enregistrer"
                : "Mettre à jour"}
          </button>
        </div>
      </form>

      {responseMessage && (
        <ResponseMessage message={responseMessage} role="doctor" />
      )}

      {/* Modals for advanced management */}
      <AnalyseModal
        isOpen={modalsManager.showAnalyseModal}
        onClose={modalsManager.closeAnalyseModal}
        analyse={analysesManager.analyseForm}
        onChange={analysesManager.setAnalyseForm}
        onSubmit={handleAnalyseSubmit}
        onSubmitMultiple={(newAnalyses) => {
          analysesManager.handleAnalysesSubmitMultiple(newAnalyses);
          modalsManager.closeAnalyseModal();
        }}
        isEditing={analysesManager.editAnalyseIndex !== null}
      />

      <Modal
        isOpen={modalsManager.showPrescriptionModal}
        onClose={modalsManager.closePrescriptionModal}
      >
        <MedicalPrescriptionInterface
          onClose={modalsManager.closePrescriptionModal}
          onMedicationsChange={setMedications}
          onAddOrEditMedication={handleAddMedication}
          medications={medications}
        />
      </Modal>
    </section>
  );
};

export default MedicalRecordFormBase;
